import axios, { AxiosResponse, isAxiosError } from "axios";
import { CompanyData } from "../../pages";
import { RequestError, setFile } from "./api";
import { Simulado } from "./simulados";
import { Treinamento } from "./treinamentos";
import { Seminario } from "./seminarios";
import { SegurancaBarragem } from "./segBarragem";

const url = `${process.env.REACT_APP_URL}/company`;

interface AllCompanies {
  items: string[]; // Ids dos empreendimentos
  page: number;
  perPage: number;
  totalPages: number;
  totalItems: number;
}

const add = async (
  data: Partial<CompanyData>,
  logo?: File,
  banner?: File,
  onProgess?: (percent: number) => void
): Promise<CompanyData | RequestError> => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_URL}/company`, {
      ...data,
    });

    const obj = processData(response.data);

    if (logo) {
      const upload = await setFile<string>(
        `${url}/logo_upload_parts/${obj.id}`,
        `${url}/logo_upload_complete/${obj.id}`,
        logo,
        onProgess
      );

      if (typeof upload !== "string") return upload;
      obj.logo = upload;
    }

    if (banner) {
      const upload = await setFile<string>(
        `${url}/banner_upload_parts/${obj.id}`,
        `${url}/banner_upload_complete/${obj.id}`,
        banner,
        onProgess
      );

      if (typeof upload !== "string") return upload;
      obj.banner = upload;
    }

    return obj;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Company Error",
        message:
          error.response?.data?.message ?? "Erro ao atualizar empreendimento",
      };
    } else {
      throw error;
    }
  }
};

const getAllNotPaginated = async (): Promise<CompanyData[] | RequestError> => {
  try {
    const data: AxiosResponse<CompanyData[]> = await axios.get(
      `${process.env.REACT_APP_URL}/company/get_all_not_page`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Company Error",
        message:
          error.response?.data.message ??
          "Erro ao obter dados dos empreendimento",
      };
    } else {
      throw error;
    }
  }
};

const getAll = async (
  name: string,
  page: number,
  perPage: number
): Promise<AllCompanies | RequestError> => {
  try {
    const data: AxiosResponse<AllCompanies> = await axios.get(
      `${process.env.REACT_APP_URL}/company/${name}/${page.toFixed(
        0
      )}/${perPage.toFixed(0)}`
    );
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Company Error",
        message:
          error.response?.data.message ??
          "Erro ao obter dados do empreendimento",
      };
    } else {
      throw error;
    }
  }
};

const company = async (id: string): Promise<CompanyData | RequestError> => {
  try {
    const data: AxiosResponse<CompanyData> = await axios.get(
      `${process.env.REACT_APP_URL}/company/${id}`
    );
    return processData(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Company Error",
        message:
          error.response?.data.message ??
          "Erro ao obter dados do empreendimento",
      };
    } else {
      throw error;
    }
  }
};

const updateCompany = async (
  id: string,
  data: Partial<CompanyData>,
  logo?: File,
  banner?: File,
  onProgess?: (percent: number) => void
): Promise<CompanyData | RequestError> => {
  try {
    const response = await axios.put(
      `${process.env.REACT_APP_URL}/company/edit/${id}`,
      {
        ...data,
      }
    );

    const obj = processData(response.data);

    if (logo) {
      const upload = await setFile<string>(
        `${url}/logo_upload_parts/${obj.id}`,
        `${url}/logo_upload_complete/${obj.id}`,
        logo,
        onProgess
      );

      if (typeof upload !== "string") return upload;
      obj.logo = upload;
    }

    if (banner) {
      const upload = await setFile<string>(
        `${url}/banner_upload_parts/${obj.id}`,
        `${url}/banner_upload_complete/${obj.id}`,
        banner,
        onProgess
      );

      if (typeof upload !== "string") return upload;
      obj.banner = upload;
    }

    return obj;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Company Error",
        message:
          error.response?.data?.message ?? "Erro ao atualizar empreendimento",
      };
    } else {
      throw error;
    }
  }
};

export interface EventoBarragem {
  tipo: "seminario" | "simulado" | "treinamento" | "segurancaBarragem";
  evento: Seminario | Simulado | Treinamento | SegurancaBarragem;
}
const getEventos = async (
  barragemId: string,
  startDate: Date,
  endDate: Date
): Promise<EventoBarragem[] | RequestError> => {
  try {
    const data: AxiosResponse<EventoBarragem[]> = await axios.get(
      `${process.env.REACT_APP_URL}/company/events/${barragemId}`,
      {
        params: {
          start: `${startDate.getFullYear()}-${(
            startDate.getMonth() + 1
          ).toSignificantDigits("mes")}-${startDate
            .getDate()
            .toSignificantDigits("dia")}`,
          end: `${endDate.getFullYear()}-${(
            endDate.getMonth() + 1
          ).toSignificantDigits("mes")}-${endDate
            .getDate()
            .toSignificantDigits("dia")}`,
        },
      }
    );

    const dados = data.data.map((val) => {
      return {
        tipo: val.tipo,
        evento: processEvent(val.evento),
      };
    });
    return dados;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Eventos Error",
        message:
          error.response?.data.message ??
          "Erro ao obter eventos para a barragem",
      };
    } else {
      throw error;
    }
  }
};

// Processar os eventos do empreendimento
function processEvent(
  value: Simulado | Treinamento | Seminario | SegurancaBarragem
) {
  value.data = new Date(value.data);
  value.dataCadastro = new Date(value.dataCadastro);
  value.dataModificacao &&= new Date(value.dataCadastro);
  if (value.anterior) {
    value.anterior.data = new Date(value.anterior.data);
    value.anterior.dataCadastro = new Date(value.anterior.dataCadastro);
    value.anterior.dataModificacao &&= new Date(value.anterior.dataCadastro);
  }

  return value;
}

function processData(value: CompanyData): CompanyData {
  return value;
}

export const ApiCompany = {
  add,
  company,
  getAll,
  getAllNotPaginated,
  updateCompany,
  getEventos,
};
